import { useContext, useState, useEffect } from 'react';
import beTwMerge from '@/utils/beTwMerge';
import ResponsiveImage from '@/utils/ResponsiveImage';
import CarouselContext from '@/context/CarouselContext';
import { trackEventWithGaEvents } from '@/utils/gaDataPush';
import { clearHTMLTags } from '@/utils/builderUtils';
import type { ICarouselCustomStyles } from './Carousel';
import type { IItem, IItemWithGAEvents } from './types';

interface ICarouselItemProps {
  item: IItem | IItemWithGAEvents;
  customItemStyles?: ICarouselCustomStyles;
  isGrid?: boolean;
  isProduct?: boolean;
  index: number;
  isActive?: boolean;
}

function isItemWithEvents(item: IItem | IItemWithGAEvents): item is IItemWithGAEvents {
  return 'gaEvents' in item;
}

export default function CarouselItem({
  item,
  customItemStyles,
  isGrid = false,
  isProduct,
  isActive = false,
  index,
}: ICarouselItemProps) {
  const { centerText, hoverImages } = useContext(CarouselContext)!;
  const hoverObject = hoverImages?.[index];
  const [activeOnce, setActiveOnce] = useState(false);
  const [description, setDescription] = useState<string>('');

  useEffect(() => {
    if (item.description) {
      setDescription(item.description);
    }
  }, [item.description]);

  useEffect(() => {
    if (isActive) {
      setActiveOnce(true);
    }
  }, [isActive]);

  return (
    <a
      href={item.link}
      className={beTwMerge(
        'tw-w-full tw-pr-0 tw-pl-0 lg:tw-pl-0 tw-inline-block hover:tw-no-underline tw-cursor-default focus:tw-outline-none tw-group',
        isGrid && 'md:tw-pl-0 lg:tw-pl-0 md:tw-inline-table',
        item.link ? 'hover:tw-text-color-[#599d8e] tw-cursor-pointer' : 'hover:tw-text-color-black'
      )}
      aria-label={item.link ? `${item.name} ${clearHTMLTags(description)}` || item.altText : undefined}
      role={item.link ? undefined : 'presentation'}
      tabIndex={item.link ? 0 : -1}
      onClick={() => {
        if (isItemWithEvents(item)) {
          item.gaEvents?.forEach(gaEvent => trackEventWithGaEvents(gaEvent, false));
        }
      }}
    >
      <div className={beTwMerge('tw-relative', customItemStyles?.container)}>
        <div
          className={beTwMerge(
            'tw-relative tw-z-10 [transition:opacity_500ms_linear] tw-opacity-0 tw-will-change-[opacity]',
            activeOnce && 'tw-opacity-100'
          )}
        >
          {hoverObject && hoverObject.hoverImage && (
            <ResponsiveImage
              loading="lazy"
              sources={hoverObject.hoverImage}
              alt={hoverObject?.hoverImageAlt || item.name}
              className={beTwMerge('!tw-hidden lg:group-hover:!tw-block', customItemStyles?.image)}
            />
          )}
          <ResponsiveImage
            loading="lazy"
            sources={item.imgSrcs}
            alt={item.altText || item.name}
            className={beTwMerge(
              customItemStyles?.image,
              hoverObject?.hoverImage ? '!tw-block lg:group-hover:!tw-hidden' : ''
            )}
          />
        </div>
        <div
          className={beTwMerge('tw-absolute tw-inset-x-0 tw-inset-y-0 tw-bg-color-grey-50 tw-pointer-events-none')}
        />
      </div>

      {item.name && (
        <h4
          className={beTwMerge(
            'tw-no-underline tw-mt-6 tw-mb-0 tw-font-medium',
            isProduct && 'tw-mt-2 tw-font-avenirmedium',
            centerText ? 'tw-text-center' : 'tw-text-left'
          )}
        >
          {item.name}
        </h4>
      )}
      <div
        className={beTwMerge(
          'tw-font-book tw-mt-2 tw-mb-0 !tw-text-color-black [&>p]:tw-mb-0',
          centerText ? 'tw-text-center' : 'tw-text-left',
          !description && 'tw-m-0'
        )}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </a>
  );
}
